// SPDX-License-Identifier: Elastic-2.0
/**
 * SidebarItem — single navigation row.
 *
 * Polymorphic across `<button>` and `<a>`:
 *  - When `href` is supplied → renders `<a href>` (used for the external
 *    `/ha` advanced link per functional spec § 4.5).
 *  - Otherwise → renders `<button type="button">` and fires `onClick`.
 *  - If both are passed, `href` wins (anchor semantics dominate).
 *
 * Icon prop (T-010 ↔ T-012 decoupling)
 * ────────────────────────────────────
 * T-012 owns the real `<Icon name=... />` and `iconPaths.ts`. This task ships
 * without depending on T-012 by accepting the icon as either:
 *
 *   - `string`     → a name like `"home"`. Renders a token-coloured stub
 *                    box with `data-icon-name={name}`, so T-020 (dashboard
 *                    layout) can either keep the stub or upgrade callsites
 *                    once T-012 lands.
 *   - `ReactNode`  → rendered as-is, letting callsites pass `<Icon ... />`
 *                    or any other glyph directly.
 *   - omitted      → no icon slot rendered.
 */
import type { MouseEvent, ReactNode } from "react";

import styles from "./Sidebar.module.css";

export interface SidebarItemProps {
  /** Icon as a string name (stubbed) OR a ReactNode (rendered directly). */
  icon?: string | ReactNode;
  /** Visible label. Hidden when the parent Sidebar is collapsed. */
  label: string;
  /** Highlights the item with the peach accent tint. */
  active?: boolean;
  /** Numeric count badge (e.g. pending suggestions). */
  count?: number;
  /** Short text badge (e.g. tier "Plus" / "Pro"). */
  badge?: string;
  /**
   * Optional right-aligned slot for chips/badges (e.g. `<TierBadge>`,
   * `<CountBadge>`). Rendered after the label inside the same flex row.
   * Hidden in collapsed mode via CSS (see `.collapsed .endSlot` rule).
   * Passed through unchanged — no i18n transform or wrapping logic.
   */
  endSlot?: ReactNode;
  /** Click handler; ignored when `href` is supplied. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** When set, renders as `<a href>` instead of `<button>`. */
  href?: string;
  /** Anchor `target` (only used when `href` is set). */
  target?: string;
  /** Anchor `rel` (only used when `href` is set). */
  rel?: string;
  /** Optional aria-label override; falls back to `label`. */
  "aria-label"?: string;
}

function isReactNode(value: unknown): value is ReactNode {
  // ReactNode is structurally too broad to runtime-check exhaustively, so
  // we treat "not a string and not undefined" as "render directly".
  return value !== undefined && typeof value !== "string";
}

function renderIcon(icon: SidebarItemProps["icon"]): ReactNode {
  if (icon === undefined) return null;
  if (typeof icon === "string") {
    return (
      <span
        className={styles.itemIcon}
        aria-hidden="true"
        data-icon-name={icon}
        style={{
          width: 18,
          height: 18,
          display: "inline-block",
          borderRadius: 4,
          background: "var(--fg-mute)",
        }}
      />
    );
  }
  if (isReactNode(icon)) {
    return (
      <span className={styles.itemIcon} aria-hidden="true">
        {icon}
      </span>
    );
  }
  return null;
}

export function SidebarItem({
  icon,
  label,
  active = false,
  count,
  badge,
  endSlot,
  onClick,
  href,
  target,
  rel,
  "aria-label": ariaLabel,
}: SidebarItemProps): JSX.Element {
  const className = [styles.item, active ? styles.itemActive : null]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {renderIcon(icon)}
      <span className={styles.itemLabel}>{label}</span>
      {typeof count === "number" && count > 0 && (
        <span className={styles.itemCount} aria-label={`${count} pending`}>
          {count}
        </span>
      )}
      {badge && (
        <span className={styles.itemBadge} aria-label={badge}>
          {badge}
        </span>
      )}
      {endSlot ? <span className={styles.endSlot}>{endSlot}</span> : null}
    </>
  );

  if (href) {
    // External-target safety: when `target="_blank"`, default rel to
    // "noopener noreferrer" if the caller didn't override it.
    const safeRel = rel ?? (target === "_blank" ? "noopener noreferrer" : undefined);
    return (
      <a
        className={className}
        href={href}
        target={target}
        rel={safeRel}
        aria-current={active ? "page" : undefined}
        aria-label={ariaLabel ?? label}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      aria-label={ariaLabel ?? label}
    >
      {content}
    </button>
  );
}
